import { useDeferredValue, useState } from 'react'
import { Loader2, MapPin, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useCompatibleUnits, useTransferDestinations } from '@/hooks/useStorageTransfers'
import { useMovePopoverContainer } from './popoverContainer'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * Choose where one occupant goes (z8nrz7f2gh) — the Moves step's location
 * picker, replacing a free-text node-id box nobody could fill in.
 *
 * Offers only free, unblocked positions at a condition the item can be kept
 * at (the server filters by the lot's material, or a product's mapped storage
 * condition), never a slot another row of
 * the plan already heads for (`claimed`, filtered here because only the wizard
 * knows the unsaved plan). Starts in the replacement unit; "Another unit"
 * lists units that have room at a compatible condition, for an item the
 * replacement cannot take.
 */
export function DestinationPicker({
  lotId,
  productId,
  defaultUnitId,
  claimed,
  current,
  disabled,
  onPick,
}: {
  lotId: string | null
  productId: string | null
  defaultUnitId: string
  claimed: Set<string>
  current: string | null
  disabled?: boolean
  onPick: (ref: string, path: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [unitId, setUnitId] = useState(defaultUnitId)
  const [search, setSearch] = useState('')
  const q = useDeferredValue(search)
  const container = useMovePopoverContainer()
  const locate = useLocationText()

  const destinations = useTransferDestinations(
    open
      ? { unitId, lotId: lotId ?? undefined, productId: productId ?? undefined, q, take: 200 }
      : null,
  )
  const requires = destinations.data?.requires ?? null
  const units = useCompatibleUnits(requires, open)
  const slots = (destinations.data?.slots ?? []).filter((s) => !claimed.has(s.ref))

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setSearch('')
      }}
    >
      <PopoverTrigger asChild>
        <Button size='sm' variant='outline' className='h-7 gap-1' disabled={disabled}>
          <MapPin className='h-3.5 w-3.5' />
          {current ? 'Change' : 'Choose slot'}
        </Button>
      </PopoverTrigger>
      {/* Portalled into the Replace dialog itself — see ExcludeMoveButton. */}
      <PopoverContent container={container} align='end' className='w-80 p-0'>
        <div className='space-y-2 border-b p-2'>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className='h-8 text-sm' aria-label='Unit'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={defaultUnitId}>
                {destinations.data?.unit.id === defaultUnitId
                  ? `${destinations.data.unit.code} (replacement)`
                  : 'Replacement unit'}
              </SelectItem>
              {(units.data?.units ?? [])
                .filter((u) => u.nodeId !== defaultUnitId)
                .map((u) => (
                  <SelectItem key={u.nodeId} value={u.nodeId}>
                    {u.code} · {u.remaining} free
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2 top-2 h-4 w-4 text-muted-foreground' />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Find a slot, e.g. R2/C04'
              aria-label='Find a slot'
              className='h-8 pl-8 text-sm'
            />
          </div>
        </div>
        <div className='max-h-64 overflow-y-auto p-1'>
          {destinations.isLoading ? (
            <p className='flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' /> Finding free slots…
            </p>
          ) : destinations.error ? (
            <p className='px-2 py-4 text-sm text-destructive'>{destinations.error.message}</p>
          ) : slots.length === 0 ? (
            <p className='px-2 py-6 text-center text-sm text-muted-foreground'>
              {search.trim()
                ? 'No free compatible slot matches.'
                : 'No free compatible slot in this unit. Try another unit, or exclude the item.'}
            </p>
          ) : (
            slots.map((s) => (
              <button
                key={s.ref}
                type='button'
                onClick={() => {
                  onPick(s.ref, s.path)
                  setOpen(false)
                  setSearch('')
                }}
                className={cn(
                  'block w-full rounded px-2 py-1.5 text-left font-mono text-[13px] hover:bg-muted',
                  s.ref === current && 'bg-primary/10',
                )}
              >
                {locate(s.path)}
              </button>
            ))
          )}
        </div>
        {destinations.data && destinations.data.total > destinations.data.slots.length && (
          <p className='border-t px-3 py-1.5 text-xs text-muted-foreground'>
            Showing {destinations.data.slots.length} of {destinations.data.total} — search to narrow.
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}
